// @flow

import * as React from 'react';
import WindowResize from '@ta/events.window-resize';
import { HasBeenVisible } from '@ta/common.is-visible';
import { BrandedHeader } from '@ta/modal.headers';
import { HeadProperties, RouteConsumer } from '@ta/common.routing';
import { localize } from '@ta/common.i18n';
import { InteractionLogger } from '@ta/tracking.interactions';
import { Icon } from '@ta/styleguide.icon';
import { connect } from 'react-redux';
import * as Import from '@ta/platform.import';
import { VideoPlayerLoadingSkeleton } from '@ta/social.skeletons';
import type { VideoPlayerPreload } from '@ta/media.video-player';

import VideoSidebar from './VideoSidebar';
import styles from './VideoPlayerAndSidebar.less';

const VIDEO_LOAD_THRESHHOLD = 400; // number of pixels threshold to load the video player
export const DEFAULT_MAX_VIDEO_HEIGHT = 450;

type VideoOnClick = (paused: boolean, play: () => void, pause: () => void) => void;

export type VideoPlayerAndSidebarProps = {|
  actor: any,
  video: any,
  maxVideoHeight?: number,
  deleteObject?: ?() => void,
  untagObject?: ?() => void,
  preload?: VideoPlayerPreload,
  autoplayWhenVisible?: boolean,
  usePoster?: boolean,
  socialStatisticsBar?: React.Node,
  socialActionBar?: React.Node,
|};

type VideoPlayerProps = {|
  ...VideoPlayerAndSidebarProps,
  onClick?: VideoOnClick,
  trackingModule?: any,
  isOpen: boolean,
  onClose?: () => void,
|};

type InnerVideoPlayerProps = {|
  ...VideoPlayerProps,
  loggedInUserId: string,
|};

type VideoPlayerState = {
  /**
   * Width the video container. used to determine what height the video should be based on video aspect ratio
   */
  containerWidth: ?number,
  /*
   * Aspect ratio as reported to us in the graphql response is, for videos uploaded before april 2018, based on
   * the thumbnail width/height so will always be 4:3. Here we store the *real* aspect ratio as determined by the video player itself
   *
   */
  realAspectRatio: ?number,

  videoFullscreen: ?boolean,
};

/**
 * Lazy loading of the video player WP-1752
 */
const [loadVideoPlayer, VideoPlayer] = Import.component.onTrigger(
  () => import('@ta/media.video-player'),
  'VideoPlayer',
  <VideoPlayerLoadingSkeleton />,
);

/**
 * "Pending" is fine to play for a user's followers, but not ok to play for the general public (on /HR for example).
 * "Conversion failure" means it's not in the CDN.  This is rare.  It's also not currently exposed via the API.
 * When a media is "removed", you can't get it from the media service anymore.
 * "Published" is fine to play for everyone
 * "Uploading" means that it is not in the CDN yet.
 */
export const isVideoAvailable = (video: any) => {
  const { statuses } = video?.object;

  if (!statuses || statuses.length === 0) {
    return false;
  }

  return !statuses.includes('UPLOADING');
};

class VideoPlayerAndSidebar extends React.Component<InnerVideoPlayerProps, VideoPlayerState> {
  static defaultProps = {
    maxVideoHeight: DEFAULT_MAX_VIDEO_HEIGHT,
    preload: 'auto',
    autoplayWhenVisible: true,
    usePoster: false,
  };

  constructor(props: InnerVideoPlayerProps) {
    super(props);
    this.state = {
      containerWidth: null,
      realAspectRatio: null,
      videoFullscreen: false,
    };
    this.contentNode = React.createRef();
  }

  componentDidMount() {
    this.computeStyles();
  }

  computeStyles = () => {
    if (this.contentNode && this.contentNode.current) {
      const containerWidth = this.contentNode.current.getBoundingClientRect().width;
      this.setState({
        containerWidth,
      });
    }
  };

  contentNode: React.ElementRef<*>;

  render() {
    const {
      actor,
      video,
      isOpen,
      deleteObject,
      untagObject,
      loggedInUserId,
      onClick,
      trackingModule,
      onClose,
      maxVideoHeight,
      preload,
      autoplayWhenVisible,
      usePoster,
      socialStatisticsBar,
      socialActionBar,
    } = this.props;

    const { realAspectRatio, videoFullscreen } = this.state;

    const { object } = video;
    // if hydration failed, or the owner isn't the current user and the video
    // is still processing, do nothing.
    if (
      !object ||
      (!actor.isMe &&
        !object.statuses.includes('PUBLISHED') &&
        !object.statuses.includes('PENDING'))
    ) {
      return null;
    }

    // videos have no min height but have max height of props.maxVideoHeight (default DEFAULT_MAX_VIDEO_HEIGHT)
    // since we know the aspect ratio of the video, and the width of the container, we calculate video height here
    const videoAspectRatio =
      realAspectRatio ||
      (object.sources && object.sources.length > 0 && object.sources[0].aspectRatio);
    const videoHeight =
      videoAspectRatio && this.state.containerWidth
        ? `${Math.min(
            maxVideoHeight || DEFAULT_MAX_VIDEO_HEIGHT,
            Math.ceil(this.state.containerWidth / videoAspectRatio),
          )}px`
        : '350px'; // arbitrary default

    const videoAvailable = isVideoAvailable(video);

    let mp4Index = video.object.sources.findIndex(src => src.url.toLowerCase().indexOf('mp4') > -1);
    mp4Index = mp4Index > -1 ? mp4Index : video.object.sources.length - 1;
    const videoStyle = videoFullscreen
      ? {
          opacity: 1,
        }
      : {};

    return (
      <React.Fragment>
        <WindowResize callback={this.computeStyles} />
        <HasBeenVisible threshold={VIDEO_LOAD_THRESHHOLD} onVisible={loadVideoPlayer}>
          {ref => (
            <div
              key={`vw_${object.id}`}
              className={`${styles.video_wrap} ${isOpen ? styles.modalView : styles.inlineView}`}
              ref={el => {
                this.contentNode.current = el;
                if (typeof ref === 'object' && ref) {
                  // eslint-disable-next-line no-param-reassign
                  ref.current = el;
                }
              }}
            >
              {/* when video is fullscreen we need to make this container's opacity 1, otherwise it is set to 0.9 and that causes weird behaviour on iPhoneX */}
              <div className={`${styles.video} ${isOpen ? 'ui_columns' : ''}`} style={videoStyle}>
                {isOpen && (
                  <div
                    className={`${
                      styles.header
                    } ui_column is-4 is-offset-8 is-3-widescreen is-offset-9-widescreen is-12-mobile`}
                  >
                    <BrandedHeader branding="TA_GREEN" />
                  </div>
                )}
                <div
                  className={
                    isOpen ? `${styles.video_wrap} ui_column is-8 is-9-widescreen is-12-mobile` : ''
                  }
                >
                  <RouteConsumer>
                    {innerRoute =>
                      innerRoute.route && innerRoute.route.modal === `Video_${video.object.id}` ? (
                        <HeadProperties
                          openGraphInfo={{
                            type: 'video.other',
                            title:
                              video.object.userProfile != null &&
                              video.object.userProfile.displayName != null
                                ? localize('social_opengraph_video_title', {
                                    username: video.object.userProfile.displayName,
                                  })
                                : '',
                            description:
                              video.object.caption != null ? `${video.object.caption}` : '',
                            url: video.object.route.url,
                            image: {
                              url:
                                video.object.posterSizes[0] != null
                                  ? `${video.object.posterSizes[0].url}`
                                  : '',
                              width:
                                video.object.posterSizes[0] != null
                                  ? video.object.posterSizes[0].width
                                  : 0,
                              height:
                                video.object.posterSizes[0] != null
                                  ? video.object.posterSizes[0].height
                                  : 0,
                            },
                            video: {
                              url: `${video.object.sources[mp4Index].url}`,
                              width: video.object.sources[mp4Index].width,
                              height: video.object.sources[mp4Index].height,
                            },
                          }}
                        />
                      ) : null
                    }
                  </RouteConsumer>
                  {/* For the Demo: Adding preload=auto and removing poster because it's too small.  It will use the first frame of the video instead.  Long term fix is MEDIA-79 */}
                  {/* poster={video.object.posterSizes[0].url} */}
                  {videoAvailable && (
                    <InteractionLogger>
                      {logFeedClick => (
                        <div style={{ height: isOpen ? '100%' : videoHeight }}>
                          <VideoPlayer
                            key={`player_${object.id}`}
                            source={video.object.sources[0].url}
                            width="100%"
                            height={isOpen ? '100%' : videoHeight}
                            preload={preload}
                            trackingModule={trackingModule}
                            mediaId={video.object.id}
                            fullscreenOnLandscape
                            autoplayWhenVisible={autoplayWhenVisible}
                            poster={usePoster ? video.object.posterSizes[0].url : undefined}
                            onFullscreenChange={fullscreen => {
                              this.setState({ videoFullscreen: fullscreen });
                            }}
                            onLoaded={(width, height) => {
                              /**
                               * Aspect ratio as reported to us in the graphql response is, for videos uploaded before april 2018, based on
                               * the thumbnail width/height so will always be 4:3. Here, we determine the real aspect ratio based on the
                               * loaded video
                               */
                              this.setState({
                                realAspectRatio: width / height,
                              });
                            }}
                            onClick={onClick}
                            onClickTracking={e => logFeedClick(e, 'feedClick')}
                          />
                        </div>
                      )}
                    </InteractionLogger>
                  )}
                  {!videoAvailable && (
                    <div
                      className={styles.video_not_available}
                      style={{ height: isOpen ? '100%' : videoHeight }}
                    >
                      <Icon name="video-fill" className={styles.video_not_available_icon} />
                    </div>
                  )}
                </div>
                {isOpen && (
                  <VideoSidebar
                    video={video}
                    actor={actor}
                    deleteObject={deleteObject}
                    untagObject={untagObject}
                    loggedInUserId={loggedInUserId}
                    onClose={onClose}
                    socialStatisticsBar={socialStatisticsBar}
                    socialActionBar={socialActionBar}
                  />
                )}
              </div>
            </div>
          )}
        </HasBeenVisible>
      </React.Fragment>
    );
  }
}

export default connect<InnerVideoPlayerProps, VideoPlayerProps, _, _, _, _>(state => ({
  loggedInUserId: state.auth.loggedInUserId,
}))(VideoPlayerAndSidebar);
